import React, { useState } from 'react';
import { Modal, Radio, Input, Button, Space, message } from 'antd';
import { rejectOpportunity, undoRejectOpportunity } from '../../services/api';
import { RejectionReason } from '../../types';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { captureEvent } from '../../services/analytics';
import css from './RejectModal.module.scss';

const { TextArea } = Input;

export interface RejectSubject {
  id: string;
  title: string;
  // Required, not optional. Users tell us the COMPANY is usually what makes a
  // job wrong for them, and two different companies advertising "Senior Backend
  // Engineer" is the ordinary case — a subject named by title alone is the one
  // thing this modal must never be ambiguous about.
  company: string;
}

// Where the decision was taken. The whole point of the row and bulk controls is
// that a user can pass on a job WITHOUT opening it; without this property we
// cannot tell whether they actually do, nor whether the cheaper paths produce
// more regret (see the undo event, which carries the same tag).
export type RejectSource = 'drawer' | 'row' | 'bulk';

interface RejectModalProps {
  // One subject for the single-reject paths, N for a bulk selection. Exactly one
  // reason is collected either way and applied to all of them — a per-row reason
  // prompt is the friction this whole branch exists to remove.
  subjects: RejectSubject[];
  open: boolean;
  onClose: () => void;
  // Fires after a reject AND again after an undo — the list changed either way,
  // and the caller refetches on both. The array is the ids that were newly
  // rejected, empty on the undo pass. A bulk reject can partially fail, so the
  // caller must key off THIS array rather than assume the whole selection went.
  onSuccess: (rejectedIds: string[]) => void;
  source: RejectSource;
}

const rejectionReasons: { value: RejectionReason; label: string }[] = [
  { value: 'not_interested', label: 'Not interested in this role' },
  { value: 'bad_fit', label: 'Not a good fit for my skills' },
  { value: 'company_concerns', label: 'Concerns about the company' },
  { value: 'salary_expectations', label: 'Salary doesn\'t meet expectations' },
  { value: 'location_mismatch', label: 'Location doesn\'t work for me' },
  { value: 'other', label: 'Other' },
];

// There is no bulk endpoint — POST/DELETE .../{id}/reject are per-id — so a bulk
// action is N requests. Four at a time: fast enough to clear a screenful, few
// enough that selecting a whole page doesn't open a socket per row.
const CONCURRENCY = 4;

interface PooledResult {
  ok: string[];
  failed: string[];
  error: unknown;
}

// Settles EVERY id and reports per-id success. Promise.all is the wrong tool:
// it rejects on the first failure (the other requests keep running, it just
// stops reporting them), so the caller learns THAT something failed and never
// WHICH — leaving some ids rejected, some not, and no way to name either set.
// That is the one outcome a bulk action must never produce.
// The shared cursor is safe: the workers interleave only at await points.
const pooled = async (
  ids: string[],
  run: (id: string) => Promise<unknown>
): Promise<PooledResult> => {
  const ok: string[] = [];
  const failed: string[] = [];
  let error: unknown = null;
  let cursor = 0;

  const worker = async () => {
    while (cursor < ids.length) {
      const id = ids[cursor++];
      try {
        await run(id);
        ok.push(id);
      } catch (e) {
        // First error wins the toast: N failures are almost always one cause
        // (offline, expired token), and N toasts would bury it.
        if (error === null) error = e;
        failed.push(id);
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, ids.length) }, worker)
  );
  return { ok, failed, error };
};

const RejectModal: React.FC<RejectModalProps> = ({
  subjects,
  open,
  onClose,
  onSuccess,
  source,
}) => {
  const [reason, setReason] = useState<RejectionReason | null>(null);
  const [otherText, setOtherText] = useState('');
  const [loading, setLoading] = useState(false);

  const count = subjects.length;
  const isBulk = count > 1;

  const handleClose = () => {
    setReason(null);
    setOtherText('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!reason || count === 0) {
      message.error('Please select a reason');
      return;
    }

    if (reason === 'other' && !otherText.trim()) {
      message.error('Please provide a reason');
      return;
    }

    setLoading(true);

    const ids = subjects.map(s => s.id);
    const payload = {
      rejectionReason: reason,
      rejectionReasonText: reason === 'other' ? otherText.trim() : undefined,
    };

    try {
      const { ok, failed, error } = await pooled(ids, id => rejectOpportunity(id, payload));

      if (failed.length > 0) {
        console.error('Reject opportunity error:', error);
      }

      // Nothing went through: this is the plain failure case. Keep the modal
      // open on the user's chosen reason so the retry is one click, not six.
      if (ok.length === 0) {
        message.error(getApiErrorMessage(error));
        return;
      }

      // One event per rejection, so the existing metric still counts rejections
      // rather than gestures; batch_size separates a bulk sweep from six
      // deliberate single rejects.
      ok.forEach(() =>
        captureEvent('opportunity_rejected', {
          rejection_reason: reason,
          source,
          batch_size: count,
        })
      );

      handleClose();
      onSuccess(ok);

      // Rejecting is reversible (DELETE .../reject) and the Rejected tab has
      // had an Undo all along — but a user triaging fast never goes there, so
      // the reversibility has to be offered where the decision is made.
      const key = `reject-${ids[0]}-${ok.length}`;

      // Mutable across clicks of this toast's Undo, deliberately. A partial undo
      // must not re-DELETE what it already restored: DELETE .../reject on an id
      // that is no longer rejected fails, so a second click over the ORIGINAL
      // set could never reach "nothing failed" — the toast would refuse to clear
      // itself forever, and the undone counts would double-count.
      let pending = [...ok];
      let restored = 0;
      // A pool per click is a pool too many; the second would race the first over
      // the same ids.
      let undoing = false;

      const undo = (
        <Button
          type="link"
          size="small"
          className={css.undoButton}
          onClick={async () => {
            if (undoing || pending.length === 0) return;
            undoing = true;
            const attempted = pending.length;
            try {
              const undone = await pooled(pending, id => undoRejectOpportunity(id));
              if (undone.ok.length === 0) {
                message.error(getApiErrorMessage(undone.error));
                return;
              }
              const done = new Set(undone.ok);
              pending = pending.filter(id => !done.has(id));
              restored += undone.ok.length;
              captureEvent('opportunity_rejection_undone', { source, count: undone.ok.length });
              if (pending.length === 0) {
                // Destroyed only AFTER everything is back. Destroying first would
                // take the Undo away on a failed request and leave no way to retry.
                message.destroy(key);
                message.success(
                  restored === 1 ? 'Rejection undone' : `${restored} rejections undone`
                );
              } else {
                // The toast deliberately survives: its Undo is the retry, and it
                // now covers only what is still rejected.
                message.warning(
                  <span>
                    Restored {undone.ok.length} of {attempted} — {getApiErrorMessage(undone.error)}
                    <br />
                    Undo again to retry the rest.
                  </span>
                );
              }
              onSuccess([]);
            } finally {
              undoing = false;
            }
          }}
        >
          Undo
        </Button>
      );

      // ONE notice, not a success toast racing an error toast: a partial result
      // is a single outcome, and it has to say what happened AND what to do
      // about it. The failures are still ticked in the list, which is also the
      // retry — but only if the copy says so.
      const notice = {
        key,
        duration: 8,
        content: failed.length === 0 ? (
          <span>
            {ok.length === 1 ? 'Opportunity rejected.' : `${ok.length} opportunities rejected.`}{' '}
            {undo}
          </span>
        ) : (
          <span>
            Rejected {ok.length} of {count}. {undo}
            <br />
            {failed.length === 1 ? '1 is' : `${failed.length} are`} still selected — try again.
          </span>
        ),
      };
      if (failed.length === 0) message.success(notice);
      else message.warning(notice);
    } finally {
      setLoading(false);
    }
  };

  const isValid = reason && (reason !== 'other' || otherText.trim());
  const submitLabel = isBulk ? `Reject ${count} opportunities` : 'Reject Opportunity';

  return (
    <Modal
      title={isBulk ? `Reject ${count} opportunities` : 'Reject Opportunity'}
      width="min(520px, 94vw)"
      open={open}
      onCancel={handleClose}
      // Sealed while the requests are in flight. Leaving these on let the user
      // dismiss a bulk reject mid-way and reasonably believe nothing happened —
      // the POSTs still landed. Worse, the in-flight submit's own close then
      // fired on whichever modal had been opened since, wiping its reason. There
      // is nothing to take back at this point; the Undo on the toast is the way
      // out, and it arrives a moment later.
      closable={!loading}
      maskClosable={!loading}
      keyboard={!loading}
      footer={null}
      className={css.rejectModal}
    >
      <p className={css.subtitle}>
        {isBulk
          ? `Why are you passing on these ${count} opportunities?`
          : `Why are you passing on "${subjects[0]?.title ?? ''}" at ${subjects[0]?.company ?? ''}?`}
      </p>

      {/* The reason applies to every selected row, so the user has to be able to
          see WHAT they are about to reject — and the company, because that is
          usually the thing that made it wrong. Capped: past a handful the list
          stops being a check and starts pushing the reasons off a phone screen. */}
      {isBulk && (
        <ul className={css.subjectList}>
          {subjects.slice(0, 5).map(s => (
            <li key={s.id}>
              {s.title}
              <span className={css.subjectCompany}> — {s.company}</span>
            </li>
          ))}
          {count > 5 && <li className={css.subjectMore}>and {count - 5} more</li>}
        </ul>
      )}

      {/* antd's RadioGroupProps doesn't type `role` (TS2322) even though the
          runtime would forward it, so the radiogroup semantics live on this
          wrapper — type-clean, no ts-expect-error. */}
      <div role="radiogroup" aria-label="Why are you passing?">
        <Radio.Group
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className={css.radioGroup}
        >
          <Space direction="vertical" size="middle">
            {rejectionReasons.map((r) => (
              <Radio key={r.value} value={r.value}>
                {r.label}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </div>

      {reason === 'other' && (
        <TextArea
          value={otherText}
          onChange={(e) => setOtherText(e.target.value)}
          placeholder="Please describe your reason..."
          // A placeholder is not a label: it disappears on the first keystroke,
          // and it was the only thing naming this field.
          aria-label="Your reason for passing"
          rows={3}
          className={css.textArea}
        />
      )}

      <div className={css.actions}>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          type="primary"
          danger
          onClick={handleSubmit}
          loading={loading}
          disabled={!isValid}
        >
          {submitLabel}
        </Button>
      </div>
    </Modal>
  );
};

export default RejectModal;
